import React, { useState } from 'react'

import { TablePagination, Theme } from '@material-ui/core'

import { Container } from '../../../components'
import { AuditTable } from '../Components'
import {
    ResidentTermDirectoryRow,
    CriteriaGroup,
    DeliverableValue,
    ResidentTermDirectoryStatus,
    EntityClass,
    MergeRequest,
} from '../../../models'
import { RowDetailModal } from '../Components/RowDetailModal/RowDetailModal'

interface Props {
    residentTermDirectories: ResidentTermDirectoryRow[]
    theme: Theme
    criteriaGroup: CriteriaGroup | undefined
    bodyH: number
    footerH: number
    isLoading: boolean
    page: number
    setPage: (page: number) => void
    rowsPerPage: number
    setRowsPerPage: (rowsPerPage: number) => void
    deliverableId: number
    leaseAuditId: number
    updateDeliverableValueMessageCount: (
        rowId: number,
        valueId: number,
        newCount: number,
    ) => void
    entityClassifications: EntityClass[]
    bulkAcceptDeliverableValues: (
        deliverableValueIds: number[],
        notes: string,
    ) => Promise<void>
    mergeResidentTermDirectories: (params: MergeRequest) => Promise<void>
    isMerging: boolean
}

export const AuditPage = ({
    theme,
    criteriaGroup,
    residentTermDirectories,
    bodyH,
    footerH,
    isLoading,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    deliverableId,
    leaseAuditId,
    updateDeliverableValueMessageCount,
    entityClassifications,
    bulkAcceptDeliverableValues,
    mergeResidentTermDirectories,
    isMerging,
}: Props) => {
    // Add sort state
    const [sortConfig, setSortConfig] = useState<{
        key: string
        direction: 'asc' | 'desc' | null
    }>({ key: '', direction: null })

    const [detailModalState, setDetailModalState] = useState<{
        open: boolean
        entityValue: DeliverableValue | null
        row: ResidentTermDirectoryRow | null
    }>({ open: false, entityValue: null, row: null })

    // Add sort function
    const onSort = (entityClassId: number) => {
        const key = entityClassId.toString()
        let direction: 'asc' | 'desc' | null = 'asc'

        if (sortConfig.key === key) {
            if (sortConfig.direction === 'asc') direction = 'desc'
            else if (sortConfig.direction === 'desc') direction = null
            else direction = 'asc'
        }

        setSortConfig({ key, direction })
    }

    // Add sorted rows logic
    const getSortedRows = (rows: ResidentTermDirectoryRow[] | undefined) => {
        if (!rows || !sortConfig.direction) return rows

        return [...rows].sort((a, b) => {
            const aValue =
                a.values.find(
                    (v) =>
                        v.entity_classification.id.toString() ===
                        sortConfig.key,
                )?.display_value || ''
            const bValue =
                b.values.find(
                    (v) =>
                        v.entity_classification.id.toString() ===
                        sortConfig.key,
                )?.display_value || ''

            if (sortConfig.direction === 'asc') {
                return aValue.localeCompare(bValue)
            }
            return bValue.localeCompare(aValue)
        })
    }

    // Update the pagination calculation to use sorted rows
    const sortedRows = getSortedRows(residentTermDirectories)
    const totalRows = sortedRows?.length || 0
    const startIndex = page * rowsPerPage
    const endIndex = startIndex + rowsPerPage
    const paginatedRows = sortedRows?.slice(startIndex, endIndex)

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const onClickCell = (
        cell: DeliverableValue,
        row: ResidentTermDirectoryRow,
    ) => {
        // First close the modal if it's open
        if (detailModalState.open) {
            setDetailModalState({
                open: false,
                entityValue: null,
                row: null,
            })
            // Then reopen it with new data after a brief delay
            setTimeout(() => {
                setDetailModalState({
                    open: true,
                    entityValue: cell,
                    row: row,
                })
            }, 100)
        } else {
            // If it wasn't open, just open it with new data
            setDetailModalState({
                open: true,
                entityValue: cell,
                row: row,
            })
        }
    }

    return (
        <Container style={{ flexDirection: 'column' }}>
            <AuditTable
                paginatedRows={paginatedRows ?? []}
                allRows={residentTermDirectories}
                theme={theme}
                criteriaGroup={criteriaGroup}
                style={{
                    flexDirection: 'column',
                    height: bodyH,
                    overflowY: 'auto',
                    overflowX: 'auto',
                    margin: theme.spacing(1),
                }}
                onSort={onSort}
                sortConfig={sortConfig}
                isLoading={isLoading}
                onClickCell={onClickCell}
                entityClassifications={entityClassifications}
                leaseAuditId={leaseAuditId}
                bulkAcceptDeliverableValues={bulkAcceptDeliverableValues}
                mergeResidentTermDirectories={mergeResidentTermDirectories}
                isMerging={isMerging}
            />
            {/* Footer with pagination */}
            <Container
                style={{
                    height: footerH,
                    borderTop: '1px solid #e0e0e0',
                    backgroundColor: theme.palette.background.paper,
                    padding: '0 24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                }}
            >
                <TablePagination
                    component="div"
                    count={totalRows}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                />
            </Container>

            <RowDetailModal
                theme={theme}
                open={detailModalState.open}
                onClose={() =>
                    setDetailModalState({
                        open: false,
                        entityValue: null,
                        row: null,
                    })
                }
                entityValue={detailModalState.entityValue}
                row={detailModalState.row}
                criteriaGroup={criteriaGroup}
                onEntitySelect={(value) =>
                    setDetailModalState((prev) => ({
                        ...prev,
                        entityValue: value,
                    }))
                }
                deliverableId={deliverableId}
                updateDeliverableValueMessageCount={
                    updateDeliverableValueMessageCount
                }
            />
        </Container>
    )
}
